import { FC, ReactNode } from "react";
import { useAtomValue } from "jotai";
import { themeState } from "@/lib/store";
import ObfuscationBanner from "@/components/data-grid/ObfuscationBanner";
import styles from "./Lockable.module.scss";

interface LockableProps {
    /** When true, the children are blurred and the upgrade banner overlays them. */
    locked: boolean;
    /** Optional minimum height so the gradient banner has room to sit over the blur. */
    minHeight?: number;
    children: ReactNode;
}

/**
 * Tier gate: blurs its children and overlays the app-wide upgrade
 * banner. Used by the revamped event page (#1633) and the Trading Idea
 * detail page (#1870).
 *
 * The blur uses the GLOBAL `obfuscated` class (`pages/globals.scss`)
 * and the existing <ObfuscationBanner>, which renders the gradient and
 * a tier-correct call to action — register for a visitor, the plan
 * picker in-app, the upgrade flow otherwise. Reuses the app-wide
 * obfuscation pattern rather than a bespoke overlay so every locked
 * surface looks and behaves the same.
 *
 * IMPORTANT: this hides content, it does not protect it. Callers must
 * only pass children the server was willing to send. On Trading Ideas
 * the server already withholds the body, evidence, catalysts, backtest
 * and quant score from anyone who fails the tier check, so the blur is
 * a rendering of that redaction — never the thing enforcing it.
 */
const Lockable: FC<LockableProps> = ({ locked, minHeight, children }) => {
    const theme = useAtomValue(themeState);

    if (!locked) return <>{children}</>;

    return (
        <div className={styles.lockable} style={minHeight ? { minHeight } : undefined}>
            <div className="obfuscated">{children}</div>
            <ObfuscationBanner theme={theme} />
        </div>
    );
};

export default Lockable;
